import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent } from "../../components/ui/card";
import { Eye, TrendingUp, Clock, AlertCircle, Users, Filter, EyeOff, CalendarCheck2 } from "lucide-react";

interface SmartViewsProps {
  leadCount?: { [key: string]: number };
  onViewChange: (viewId: string) => void;
  showactiveview?: string;
}

const SmartViews = ({ leadCount, onViewChange, showactiveview }: SmartViewsProps) => {
  const [activeView, setActiveView] = useState(showactiveview || 'all');

  useEffect(() => {
    if (showactiveview) {
      setActiveView(showactiveview);
    }
  }, [showactiveview]);

  const smartViews = useMemo(() => [
    {
      id: 'all',
      name: 'All Leads',
      description: 'Complete lead database',
      icon: <Users className="h-4 w-4" />,
      count: leadCount?.all,
      tone: 'gold',
      leads: 'all',
    },
    {
      id: 'interested',
      name: 'Interested',
      description: 'Showing interest',
      icon: <Eye className="h-4 w-4" />,
      count: leadCount?.interested,
      tone: 'mint',
      leads: 'interested',
    },
    {
      id: 'meetings',
      name: 'Meeting Setup',
      description: 'Scheduled meetings',
      icon: <CalendarCheck2 className="h-4 w-4" />,
      count: leadCount?.meetings,
      tone: 'blue',
      leads: 'meetingSetup',
    },
    {
      id: 'proposals',
      name: 'Proposal Sent',
      description: 'Awaiting decision',
      icon: <Filter className="h-4 w-4" />,
      count: leadCount?.proposals,
      tone: 'violet',
      leads: 'proposalSent',
    },
    {
      id: 'warm',
      name: 'Warm Leads',
      description: 'No activity for 30+ days',
      icon: <Clock className="h-4 w-4" />,
      count: leadCount?.warm,
      tone: 'amber',
      leads: 'warm',
    },
    {
      id: 'hot',
      name: 'Hot Leads',
      description: 'High priority prospects',
      icon: <TrendingUp className="h-4 w-4" />,
      count: leadCount?.hot,
      tone: 'orange',
      leads: 'hot',
    },
    {
      id: 'overdue',
      name: 'Overdue Follow-up',
      description: 'Past due follow-up dates',
      icon: <AlertCircle className="h-4 w-4" />,
      count: leadCount?.overdue,
      tone: 'rose',
      leads: 'overdue',
    },
    {
      id: 'unresponsive',
      name: 'Unresponsive',
      description: 'Not responding to calls',
      icon: <EyeOff className="h-4 w-4" />,
      count: leadCount?.unresponsive,
      tone: 'pink',
      leads: 'unresponsive',
    },
  ], [leadCount]);

  const toneClasses: Record<string, { card: string; icon: string; border: string }> = {
    gold: {
      card: 'border-[#d7e7ea] bg-gradient-to-br from-white via-[#ffffff] to-[#f6fbfb]',
      icon: 'text-[#2f8d8f] bg-[#eef8f8]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    mint: {
      card: 'border-[#dbece2] bg-gradient-to-br from-white via-[#ffffff] to-[#f5fff8]',
      icon: 'text-[#17a34a] bg-[#ecfdf3]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    blue: {
      card: 'border-[#d8e1f5] bg-gradient-to-br from-white via-[#ffffff] to-[#f6f9ff]',
      icon: 'text-[#3366ff] bg-[#eef3ff]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    violet: {
      card: 'border-[#e5d9f1] bg-gradient-to-br from-white via-[#ffffff] to-[#fbf7ff]',
      icon: 'text-[#8b5cf6] bg-[#f4edff]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    amber: {
      card: 'border-[#eee1bf] bg-gradient-to-br from-white via-[#ffffff] to-[#fffaf0]',
      icon: 'text-[#d97706] bg-[#fff4e6]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    orange: {
      card: 'border-[#eed8c4] bg-gradient-to-br from-white via-[#ffffff] to-[#fff8f2]',
      icon: 'text-[#f97316] bg-[#fff1e8]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    rose: {
      card: 'border-[#efd7d7] bg-gradient-to-br from-white via-[#ffffff] to-[#fff7f7]',
      icon: 'text-[#ef4444] bg-[#fff1f1]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
    pink: {
      card: 'border-[#ead7e2] bg-gradient-to-br from-white via-[#ffffff] to-[#fff7fb]',
      icon: 'text-[#db2777] bg-[#fff0f7]',
      border: 'shadow-[0_1px_0_rgba(15,23,42,0.04)]',
    },
  };

  const handleViewClick = (view: { id: string; leads: string }) => {
    setActiveView(view.leads);
    onViewChange(view.leads);
  };

  return (
    <Card className="rounded-[22px] border border-slate-200 bg-white shadow-[0_10px_24px_rgba(15,23,42,0.04)]">
      <CardContent className="p-3.5 md:p-4">
        <div className="grid grid-cols-1 gap-2.5 md:grid-cols-2 xl:grid-cols-4">
          {smartViews.map((view) => {
            const isActive = activeView === view.id || activeView === view.leads;

            return (
              <button
                key={view.id}
                type="button"
                className={`group relative flex min-h-[92px] flex-col justify-between rounded-[18px] border px-4 py-3 text-left transition-all duration-200 hover:-translate-y-0.5 hover:shadow-[0_10px_24px_rgba(15,23,42,0.06)] ${toneClasses[view.tone].card} ${toneClasses[view.tone].border} ${
                  isActive
                    ? '!border-[rgba(185,141,41,0.8)] !bg-[#fffdf3] !bg-none ring-1 ring-[rgba(185,141,41,0.2)] shadow-[0_12px_28px_rgba(185,141,41,0.10)]'
                    : ''
                }`}
                style={isActive ? { backgroundImage: 'none' } : undefined}
                onClick={() => handleViewClick(view)}
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[#8ea0b6]">
                      {view.name}
                    </p>
                    <div className="mt-1.5 text-[26px] font-semibold leading-none tracking-tight text-slate-900">
                      {view.count ?? 0}
                    </div>
                  </div>
                  <div className={`flex h-8 w-8 items-center justify-center rounded-full ${toneClasses[view.tone].icon}`}>
                    {view.icon}
                  </div>
                </div>
                <div className="mt-2">
                  <p className="text-[12px] text-[#7286a3]">{view.description}</p>
                </div>
              </button>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
};

export default SmartViews;
